import { h, JSX } from 'preact';
import classNames from 'classnames';
import { Modal } from './Modal';

export interface FiscalOpenSaleModalProps {
    title: string;
    message: string;
    efrMessage?: string;
    efrMessageLabel: string;
    invoiceLabel: string;
    invoice: string;
    totalLabel: string;
    total: string;
    retryLabel: string;
    cancelLabel: string;
    busyLabel: string;
    canRetry: boolean;
    /** Which request is running, if any; both buttons are disabled meanwhile. */
    busyAction?: 'retry' | 'cancel';
    onRetry: () => void;
    onCancel: () => void;
}

/**
 * A box office sale the EFR has not signed. It cannot be dismissed: the cashier settles it with
 * Retry or Cancel sale, and the cash register sells nothing else until then.
 */
export const FiscalOpenSaleModal = (props: FiscalOpenSaleModalProps): JSX.Element => {
    const isBusy = props.busyAction !== undefined;

    const buttonClass = (activeClass: string): string =>
        classNames('grow text-base px-4 py-6 font-semibold min-w-[160px] btn', isBusy ? 'btn-disabled' : activeClass);

    return (
        <Modal show title={props.title} toggleModal={(): void => undefined} hasCloseButton={false}>
            <div class="px-8 pt-6" aria-live="polite">
                <p class="mb-4">{props.message}</p>
                {props.efrMessage && (
                    <p class="mb-4 text-sm">
                        <span class="font-semibold">{props.efrMessageLabel}:</span> {props.efrMessage}
                    </p>
                )}
                <div class="flex justify-between border-t pt-6">
                    <span>{props.invoiceLabel}</span>
                    <span class="font-semibold">{props.invoice}</span>
                </div>
                <div class="flex justify-between mt-2">
                    <span>{props.totalLabel}</span>
                    <span class="font-semibold">{props.total}</span>
                </div>
            </div>
            <div class="flex items-center justify-center gap-6 py-12 px-16">
                <button
                    type="button"
                    className={buttonClass('btn-red')}
                    disabled={isBusy}
                    aria-busy={props.busyAction === 'cancel'}
                    onClick={props.onCancel}
                >
                    {props.busyAction === 'cancel' ? props.busyLabel : props.cancelLabel}
                </button>
                {props.canRetry && (
                    <button
                        type="button"
                        className={buttonClass('btn-light-blue')}
                        disabled={isBusy}
                        aria-busy={props.busyAction === 'retry'}
                        onClick={props.onRetry}
                    >
                        {props.busyAction === 'retry' ? props.busyLabel : props.retryLabel}
                    </button>
                )}
            </div>
        </Modal>
    );
};
